import React, { useCallback, useMemo, useState } from 'react';

import { hasAnyReportCardAction, reportCardActionOptions } from './report-card-access.helpers';
import { LoadedReportCardActions } from './loaded-report-card-actions.component';
import { ReportCardActionsButton } from './report-card-actions-button.component';
import { useReportCardActionsContext } from './report-card-actions.context';

interface IOnDemandReportCardActionsProps {
  // Needs _id, ageGroup { _id } and reportCardAccess; the contacts and links load on the first click.
  playerAssessment: IPlayerAssessment;
}

// A long table's report card menu: the row carries only the status, and a player's contacts and links load
// when their menu is first opened. See "The player list loads report card links on demand" in CLAUDE.md.
export const OnDemandReportCardActions: React.FC<IOnDemandReportCardActionsProps> = ({ playerAssessment }) => {
  const { surfaceFor } = useReportCardActionsContext();
  const [requested, setRequested] = useState(false);
  const { canManage, canOpen } = surfaceFor(playerAssessment.ageGroup?._id);

  // Assumes the player has links, so the trigger shows whenever some action might apply once they load.
  const mayHaveActions = useMemo(() => hasAnyReportCardAction(reportCardActionOptions(playerAssessment.reportCardAccess, {
    canManage, canOpen, hasCopyableLink: true, hasExpiringLinks: true,
  })), [playerAssessment.reportCardAccess, canManage, canOpen]);

  const load = useCallback(() => setRequested(true), []);
  const reset = useCallback(() => setRequested(false), []);

  if (requested) return <LoadedReportCardActions playerAssessmentId={playerAssessment._id} onFailed={reset} />;
  if (!mayHaveActions) return null;

  return <ReportCardActionsButton playerAssessmentId={playerAssessment._id} onClick={load} />;
};
